import { AvatarGroup, Box, Divider, Flex, Tooltip } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { setIsOpen } from 'components/Develop/competencyDrawerSlice';
import { useInitiativeAccessControl } from 'components/RabbitInitiativeSidePanel/accessControls/useInitiativeAccessControl';
import { INITIATIVE_STATUS_OPTIONS } from 'components/RabbitInitiativeSidePanel/constants';
import { QUERY_KEYS } from 'components/RabbitInitiativeSidePanel/datahooks';
import { closeSecondaryPanel } from 'components/RabbitInitiativeSidePanel/initiativeSecondaryPanelSlice';
import { resetKR } from 'components/RabbitOKRSidePanel/KRslice';
import { checkDetailsModified } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/ChangeDetails/ChangeDetails';
import { checkCheckInValuesModified } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/CheckIn/CheckIn';
import TooltipLabelList from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/TooltipLabelList';
import { StatusTag } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Overview/OKRStatus';
import { useOKRTagsQuery } from 'components/RabbitOKRSidePanel/datahooks/useGetCustomTagsOrganization';
import { useEmployeeGoalsPrivileges } from 'components/hooks/useEmployeeGoalsPrivileges';
import useUnsavedDataAlert from 'components/hooks/useUnsavedDataAlert';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TASK_STATUS_KEYS } from 'screens/home/summary/components/Cards/Initiatives/InitiativesMySummaryCard';
import { kRDrawerSelector } from 'selectors/kRDrawer';

import { Done, InitiativesPanel, OpenSidePanel } from 'assets/assetsV1/iconComponents';

import { GOAL_VISIBILITY_OPTIONS } from 'constants/ProjectConstants';
import { getEncodedPanelStack, getParsedPanelStack, getQueryParams } from 'constants/helper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';

import { Avatar } from 'uiToolkitV1/Avatar';
import { DateComp } from 'uiToolkitV1/DateComp';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { useUpdateInitiativeStatusMutation } from '../../../datahooks';
import InitiativeMenuOptions from './InitiativeMenuOptions';

export const InitiativeTaskRow = ({
  id,
  goalName,
  taskStatus,
  subTasksCount = 0,
  dueDate,
  employees = [],
  showDivider,
  data,
  status,
  panelType = 'GOAL',
  parentId = null,
  isOKRPanel = false,
  visibilityType,
  employeeId = null,
  isOKRPanelDisabled,
}) => {
  const history = useHistory();
  const DateText = ({ children }) => <TextLabel bold>{children}</TextLabel>;

  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const okrDetails = useSelector(kRDrawerSelector);
  const { uuid, localCheckInStateValues, localStateValues } = okrDetails;
  const { showUnsavedDataAlertDialog } = useUnsavedDataAlert();
  const { data: customTags = [] } = useOKRTagsQuery({ okrId: uuid });

  const isCheckInValuesModified = checkCheckInValuesModified(localCheckInStateValues, okrDetails);

  const isCommentModified = localCheckInStateValues?.isCommentModified || false;
  const isDetailsChanged = checkDetailsModified(localStateValues, okrDetails, customTags);

  const isUnsavedChangesInOKR = isCheckInValuesModified || isCommentModified || isDetailsChanged;

  const { mutate } = useUpdateInitiativeStatusMutation();

  const { canEditInitiativePrivilege } = useInitiativeAccessControl();
  const { canEditAllInitiativePrivilege, canEditPublicInitiativePrivilege } = useEmployeeGoalsPrivileges();

  const viewDetailsPanel = () => {
    const otherQueryParams = getQueryParams(history.location.search);
    const parserPanelStack = getParsedPanelStack({ location: history.location });
    const routeWithPanelStack = getEncodedPanelStack({
      panelStack: parserPanelStack ?? [],
      type: panelType,
      id: parentId,
      objectivePId: panelType !== 'OBJECTIVE' ? null : parentId,
      otherQueryParams: {
        ...otherQueryParams,
        isCompetencyPanelOpen: 'false',
        ids: id,
        empId: employeeId,
        type: 'TASK',
      },
    });

    history.push({
      search: routeWithPanelStack,
    });
    dispatch(closeSecondaryPanel());

    // close competency panel
    dispatch(setIsOpen(false));
  };

  const onInitiativeClick = (...props) => {
    if (isUnsavedChangesInOKR) {
      showUnsavedDataAlertDialog({
        onCancelCallback: () => {
          dispatch(resetKR());

          viewDetailsPanel();
        },
      });
    } else {
      dispatch(resetKR());

      viewDetailsPanel();
    }
  };

  const menuOptions = [
    {
      label: 'View Details',
      onClick: () => {
        onInitiativeClick();
      },
      accessFn: () => status === INITIATIVE_STATUS_OPTIONS[0].value,
      Icon: OpenSidePanel,
    },
    {
      label: 'Mark Complete',
      onClick: () => {
        const updatedData = { ...data, completionTS: new Date().valueOf() };
        mutate(
          { updates: updatedData },
          {
            onError: (err) => {
              console.error(err.message);
              dispatch(showErrorSnackbar(err.message || `Failed to mark an initiative complete.`));
            },
            onSuccess: (data) => {
              const newlyCreatedInitiative = data?.entity;
              queryClient.invalidateQueries([QUERY_KEYS.INITIATIVE_SUBTASKS]);
              queryClient.invalidateQueries([REACT_QUERY_KEYS.competencyDrawerNew.competencyTasks]);
              dispatch(showSuccessSnackbar(`Initiative marked completed.`));
            },
          }
        );
      },
      accessFn: () =>
        panelType === 'COMPETENCY' ||
        (isOKRPanel
          ? visibilityType === GOAL_VISIBILITY_OPTIONS[0].value
            ? canEditAllInitiativePrivilege
            : canEditPublicInitiativePrivilege
          : canEditInitiativePrivilege),
      Icon: Done,
    },
  ];

  const filteredMenuOptions = menuOptions.filter(({ accessFn }) => accessFn());

  const employeesCollaboratorsFirst = employees?.sort((a, b) => {
    if (a.ownerType < b.ownerType) {
      return 1;
    }
    if (a.ownerType > b.ownerType) {
      return -1;
    }
    return 0;
  });

  const toIds = employeesCollaboratorsFirst?.map((employeeData) => ({
    displayName: employeeData.metadata?.displayName,
  }));

  return (
    <StyledRowContainer>
      <Flex paddingTop={4} justifyContent="space-between">
        <Flex
          pl={3}
          width="100%"
          onClick={() => {
            if (isOKRPanelDisabled) return;
            onInitiativeClick();
          }}
          columnGap={2}
        >
          <Box>
            <InitiativesPanel />
          </Box>
          <TextBodyMedium
            style={
              status === INITIATIVE_STATUS_OPTIONS[1].value
                ? {
                    textDecoration: 'line-through',
                  }
                : {}
            }
          >
            {goalName}
          </TextBodyMedium>
        </Flex>
        {!isOKRPanelDisabled && (
          <Flex alignItems="center">
            <InitiativeMenuOptions menuOptions={filteredMenuOptions} />
          </Flex>
        )}
      </Flex>

      <Flex
        onClick={() => {
          if (isOKRPanelDisabled) return;
          onInitiativeClick();
        }}
        paddingBottom={4}
        justifyContent="space-between"
      >
        <Tooltip label={<TooltipLabelList labelList={toIds} />}>
          <StyledAvatarGroup pl={9}>
            {employeesCollaboratorsFirst?.map((employeeData) => (
              <Avatar
                sx={{
                  border: employeeData.ownerType === 'OWNER' ? '1px solid #60C861 !important' : '0',
                }}
                name={employeeData.metadata?.displayName}
                src={employeeData.profile?.profilePhoto}
                key={id}
                mr="4px !important"
              />
            ))}
          </StyledAvatarGroup>
        </Tooltip>
        <Flex justifyContent="space-between" alignItems="center" mr="16px">
          <Flex alignItems="center">
            <StatusTag mr={subTasksCount > 0 || dueDate ? '8px' : '0px'} bg={TASK_STATUS_KEYS[taskStatus]?.tagColor}>
              <TextLabel bold color={TASK_STATUS_KEYS[taskStatus]?.color}>
                {TASK_STATUS_KEYS[taskStatus]?.label}
              </TextLabel>
            </StatusTag>
            {subTasksCount > 0 && (
              <TextLabel bold>
                {subTasksCount} {subTasksCount > 1 ? 'Sub Tasks' : 'Sub Task'} &nbsp; • &nbsp;
              </TextLabel>
            )}
            {dueDate && (
              <DateComp
                date={dueDate}
                // isRelativeDateFormat
                // enableRelativeFormatDistanceColor
                textComponent={DateText}
                // forceRelativeDateFormat
                // showDateInBrackets
              />
            )}
          </Flex>
        </Flex>
      </Flex>

      {showDivider && <Divider color="neutral.CFD7DF" />}
    </StyledRowContainer>
  );
};

const StyledAvatarGroup = styled(AvatarGroup)`
  display: flex;
`;

const StyledRowContainer = styled(Box)`
  &:hover {
    background-color: #f6fafe;
    transition: 0.4s ease-in-out;
  }
  cursor: pointer;
`;
